/**
 * The renderer's access point to the Electron shell.
 *
 * This is the ONLY module in `src/` that knows `window.pointcraftElectron`
 * exists. Everything else goes through the platform services in
 * `src/platform/services/`, so the shell stays an implementation detail of the
 * composition root rather than a global the app reaches for.
 *
 * **Types only across the boundary.** The contract is imported with
 * `import type` from `electron-shared/bridge-types` (a tsconfig path alias to
 * `electron/shared/bridge-types.ts`). Type-only imports are erased before Vite
 * sees them, so no `electron/**` code can end up in `dist-pos` — which is what
 * keeps the five web bundles byte-for-byte unaffected by the desktop work.
 *
 * **Fail closed, always.** Every helper here throws
 * `ElectronBridgeUnavailableError` rather than returning a degraded answer.
 * Hazard F2 is that a missing desktop implementation quietly resolves to a
 * Capacitor *web* implementation — mock printers that report success, plaintext
 * localStorage reported as `source: 'native'`. A register that cannot print is
 * recoverable; one that believes it printed is not.
 */
import type {
  ShellBridge,
  EscPosBridge,
  BridgeResult,
  BridgeSurface,
  PaymentsBridge,
  StarPrintBridge,
  SecureStorageBridge,
  PointcraftElectronBridge,
} from 'electron-shared/bridge-types';

import { ElectronBridgeUnavailableError } from '../errors';

/** The bridge major this renderer build speaks. Must match the preload's. */
export const SUPPORTED_BRIDGE_VERSION = 1;

declare global {
  interface Window {
    pointcraftElectron?: PointcraftElectronBridge;
  }
}

export type { BridgeResult } from 'electron-shared/bridge-types';

/**
 * The bridge, or a typed failure.
 *
 * The version check is a refusal, not a warning: a preload from a different
 * major may have renamed a channel or changed a payload shape, and continuing
 * against it means silently calling the wrong thing with the right-looking
 * arguments.
 */
export function getElectronBridge(surface: BridgeSurface): PointcraftElectronBridge {
  const bridge = typeof window === 'undefined' ? undefined : window.pointcraftElectron;

  if (!bridge) {
    throw new ElectronBridgeUnavailableError(
      surface,
      `The Electron bridge surface '${surface}' is unavailable: window.pointcraftElectron is ` +
        'absent. Refusing to fall back to a browser implementation.'
    );
  }

  if (bridge.bridgeVersion !== SUPPORTED_BRIDGE_VERSION) {
    throw new ElectronBridgeUnavailableError(
      surface,
      `The Electron shell speaks bridge version ${bridge.bridgeVersion}, but this build of the ` +
        `POS speaks ${SUPPORTED_BRIDGE_VERSION}. Refusing to call across a mismatched contract.`
    );
  }

  return bridge;
}

/** True when a usable shell bridge is present. Never throws. */
export function hasElectronBridge(): boolean {
  try {
    getElectronBridge('shell');
    return true;
  } catch {
    return false;
  }
}

export const shellBridge = (): ShellBridge => getElectronBridge('shell').shell;
export const secureStorageBridge = (): SecureStorageBridge =>
  getElectronBridge('secureStorage').secureStorage;
export const starPrintBridge = (): StarPrintBridge => getElectronBridge('starPrint').starPrint;
export const paymentsBridge = (): PaymentsBridge => getElectronBridge('payments').payments;
export const escPosBridge = (): EscPosBridge => getElectronBridge('escPos').escPos;

/**
 * Turns an envelope into a value or a throw.
 *
 * Callers keep their existing `try`/`catch` semantics — `secure-storage.service`,
 * `printingService` and the payment engine all already handle typed rejections
 * — so adopting the bridge does not mean rewriting their error handling.
 *
 * The shell's error code is carried onto the thrown error so a caller that
 * *does* care (T3.C needs `SECURE_STORAGE_UNAVAILABLE` to mark
 * `nativeReadFailed`; T3.B needs `PAYMENT_DUPLICATE_DISPATCH` to convert a
 * charge into a status query) can branch on it without string matching.
 */
export function unwrapBridgeResult<T>(surface: BridgeSurface, result: BridgeResult<T>): T {
  if (result.ok) return result.value;
  throw bridgeFailure(surface, result.error.code, result.error.message);
}

/** A typed bridge failure carrying the shell's error code. */
export function bridgeFailure(
  surface: BridgeSurface,
  code: string,
  message: string
): ElectronBridgeUnavailableError {
  const error = new ElectronBridgeUnavailableError(surface, message);
  Object.defineProperty(error, 'bridgeErrorCode', { value: code, enumerable: true });
  return error;
}

/** The shell's error code from a thrown bridge error, when there is one. */
export function bridgeErrorCodeOf(error: unknown): string | null {
  if (typeof error !== 'object' || error === null) return null;
  const code = (error as { bridgeErrorCode?: unknown }).bridgeErrorCode;
  return typeof code === 'string' ? code : null;
}
